import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Edit, Trash2, Plus, User, CreditCard, Package, Search } from 'lucide-react';

interface Plan {
  id: string;
  name: string;
  credits: number;
  price_monthly: number;
  price_yearly: number;
}

interface Profile {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  created_at: string;
  roles: string[];
  referrer_url?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  ref_param?: string;
  landing_page?: string;
  first_visit_at?: string;
  subscription?: {
    credits_used: number;
    credits_remaining: number;
    status: string;
    billing_cycle: string;
    expires_at: string;
    plan_id: string;
    plans?: {
      name: string;
      credits: number;
    };
  };
}

interface Props {
  profiles: Profile[];
  onRefresh: () => void;
}

export function UserManagement({ profiles, onRefresh }: Props) {
  const [editingUser, setEditingUser] = useState<Profile | null>(null);
  const [fullName, setFullName] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [creditsToAdd, setCreditsToAdd] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [searchEmail, setSearchEmail] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  const ITEMS_PER_PAGE = 10;

  // Filter profiles based on email search and sort by registration date (newest first)
  const filteredProfiles = profiles
    .filter(profile =>
      profile.email.toLowerCase().includes(searchEmail.toLowerCase())
    )
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  // Calculate pagination
  const totalPages = Math.ceil(filteredProfiles.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedProfiles = filteredProfiles.slice(startIndex, endIndex);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchEmail]);

  useEffect(() => {
    fetchPlans();
    
    // Listen for credits updates to refresh admin data
    const handleCreditsUpdate = () => {
      onRefresh();
    };

    window.addEventListener('creditsUpdated', handleCreditsUpdate);
    return () => window.removeEventListener('creditsUpdated', handleCreditsUpdate);
  }, [onRefresh]);

  const fetchPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('plans')
        .select('*')
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      setAvailablePlans(data || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load plans');
    }
  };

  const handleEditUser = (profile: Profile) => {
    setEditingUser(profile);
    setFullName(profile.full_name || '');
    setSelectedRole('');
    setCreditsToAdd('');
    setSelectedPlan('');
    setSelectedBillingCycle('monthly');
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    try {
      // Update profile
      if (fullName !== editingUser.full_name) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ full_name: fullName })
          .eq('user_id', editingUser.user_id);

        if (profileError) throw profileError;
      }

      // Add role if selected
      if (selectedRole) {
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({ user_id: editingUser.user_id, role: selectedRole });

        if (roleError && !roleError.message.includes('duplicate')) {
          throw roleError;
        }
      }

      // Assign plan if selected
      if (selectedPlan) {
        const plan = availablePlans.find(p => p.id === selectedPlan);
        if (plan) {
          // Check if user has existing subscription
          const { data: existingSub } = await supabase
            .from('user_subscriptions')
            .select('*')
            .eq('user_id', editingUser.user_id)
            .single();

          const subscriptionData = {
            user_id: editingUser.user_id,
            plan_id: selectedPlan,
            status: 'active',
            billing_cycle: selectedBillingCycle,
            credits_remaining: plan.credits === -1 ? null : plan.credits,
            credits_used: 0,
            expires_at: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() // 1 year from now
          };

          if (existingSub) {
            // Update existing subscription
            const { error: subError } = await supabase
              .from('user_subscriptions')
              .update(subscriptionData)
              .eq('user_id', editingUser.user_id);
            
            if (subError) throw subError;
          } else {
            // Create new subscription
            const { error: subError } = await supabase
              .from('user_subscriptions')
              .insert(subscriptionData);
            
            if (subError) throw subError;
          }
          
          toast.success(`Plan "${plan.name}" assigned successfully`);
        }
      }

      // Add credits if specified (for existing subscriptions)
      if (creditsToAdd && editingUser.subscription) {
        const currentCredits = editingUser.subscription.credits_remaining || 0;
        const newCredits = currentCredits + parseInt(creditsToAdd);

        const { error: creditsError } = await supabase
          .from('user_subscriptions')
          .update({ credits_remaining: newCredits })
          .eq('user_id', editingUser.user_id);

        if (creditsError) throw creditsError;
        
        toast.success(`Added ${creditsToAdd} credits`);
      }

      toast.success('User updated successfully');
      setEditingUser(null);
      onRefresh();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      // Delete from auth.users will cascade to other tables
      const { error } = await supabase.auth.admin.deleteUser(userId);
      
      if (error) throw error;
      
      toast.success('User deleted successfully');
      onRefresh();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleRemoveRole = async (userId: string, role: string) => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('role', role);

      if (error) throw error;
      
      toast.success(`Role '${role}' removed successfully`);
      onRefresh();
    } catch (error) {
      console.error('Error removing role:', error);
      toast.error('Failed to remove role');
    }
  };

  return (
    <div className="space-y-4">
      {/* Search Controls */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by email..."
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="text-sm text-muted-foreground">
          {filteredProfiles.length} of {profiles.length} users
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Roles</TableHead>
            <TableHead>Credits</TableHead>
            <TableHead>Plan Status</TableHead>
            <TableHead>Use Case</TableHead>
            <TableHead>Onboarding</TableHead>
            <TableHead>Referral Source</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedProfiles.map((profile) => (
            <TableRow key={profile.id}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  {profile.full_name || 'No name'}
                </div>
              </TableCell>
              <TableCell>{profile.email}</TableCell>
              <TableCell>
                <div className="flex gap-1 flex-wrap">
                  {profile.roles.length > 0 ? (
                    profile.roles.map((role, index) => (
                      <Badge 
                        key={index} 
                        variant={role === 'admin' ? 'default' : 'secondary'}
                        className="cursor-pointer"
                        onClick={() => handleRemoveRole(profile.user_id, role)}
                      >
                        {role} ×
                      </Badge>
                    ))
                  ) : (
                    <Badge variant="outline">No roles</Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {profile.subscription ? (
                  <div className="flex items-center gap-2 text-sm">
                    <CreditCard className="w-4 h-4 text-muted-foreground" />
                    {profile.subscription.credits_remaining === null ? (
                      <span className="text-success">∞</span>
                    ) : (
                      <>
                        <span className="text-muted-foreground">
                          {profile.subscription.credits_used || 0}
                        </span>
                        <span className="text-muted-foreground">/</span>
                        <span className="text-success">
                          {(profile.subscription.credits_used || 0) + profile.subscription.credits_remaining}
                        </span>
                      </>
                    )}
                  </div>
                ) : (
                  <Badge variant="outline">No subscription</Badge>
                )}
              </TableCell>
              <TableCell>
                {profile.subscription ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={profile.subscription.status === 'active' ? 'default' : 'secondary'}>
                        {profile.subscription.status}
                      </Badge>
                      <Badge variant="outline">
                        {profile.subscription.billing_cycle}
                      </Badge>
                    </div>
                    {profile.subscription.plans && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Package className="w-3 h-3" />
                        {profile.subscription.plans.name}
                      </div>
                    )}
                  </div>
                ) : (
                  <Badge variant="outline">No plan</Badge>
                )}
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  {(profile as any).use_case ? (
                    <Badge variant="secondary" className="capitalize">
                      {(profile as any).use_case}
                    </Badge>
                  ) : (
                    <Badge variant="outline">Not specified</Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant={(profile as any).onboarding_completed ? 'default' : 'secondary'}>
                      {(profile as any).onboarding_completed ? 'Completed' : 'Pending'}
                    </Badge>
                  </div>
                  {(profile as any).demo_watched !== undefined && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      Demo: {(profile as any).demo_watched ? '✓ Watched' : '✗ Not watched'}
                    </div>
                  )}
                  {(profile as any).selected_plan && (
                    <div className="text-xs text-muted-foreground">
                      Selected: {(profile as any).selected_plan}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  {profile.utm_source || profile.ref_param || profile.referrer_url ? (
                    <div className="space-y-1">
                      <div className="font-medium">
                        {profile.utm_source && <Badge variant="secondary" className="mr-1">{profile.utm_source}</Badge>}
                        {profile.ref_param && <Badge variant="outline" className="mr-1">ref: {profile.ref_param}</Badge>}
                        {!profile.utm_source && !profile.ref_param && profile.referrer_url && (
                          <Badge variant="outline">{new URL(profile.referrer_url).hostname}</Badge>
                        )}
                      </div>
                      {profile.utm_campaign && (
                        <div className="text-xs text-muted-foreground">
                          Campaign: {profile.utm_campaign}
                        </div>
                      )}
                      {profile.landing_page && (
                        <div className="text-xs text-muted-foreground">
                          Landing: {new URL(profile.landing_page).pathname}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Badge variant="outline">Direct</Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditUser(profile)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Edit User</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">Full Name</label>
                          <Input
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            placeholder="Enter full name"
                          />
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium">Add Role</label>
                          <Select value={selectedRole} onValueChange={setSelectedRole}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select role to add" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="moderator">Moderator</SelectItem>
                              <SelectItem value="user">User</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className="text-sm font-medium">Assign Plan</label>
                          <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select plan to assign" />
                            </SelectTrigger>
                            <SelectContent>
                              {availablePlans.map((plan) => (
                                <SelectItem key={plan.id} value={plan.id}>
                                  {plan.name} - {plan.credits === -1 ? 'Unlimited' : `${plan.credits} credits`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {selectedPlan && (
                          <div>
                            <label className="text-sm font-medium">Billing Cycle</label>
                            <Select value={selectedBillingCycle} onValueChange={setSelectedBillingCycle}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="monthly">Monthly</SelectItem>
                                <SelectItem value="yearly">Yearly</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {editingUser?.subscription && (
                          <div>
                            <label className="text-sm font-medium">Add Credits</label>
                            <Input
                              type="number"
                              value={creditsToAdd}
                              onChange={(e) => setCreditsToAdd(e.target.value)}
                              placeholder="Credits to add"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Current: {editingUser.subscription.credits_remaining === null ? 'Unlimited' : `${editingUser.subscription.credits_remaining} credits`}
                            </p>
                          </div>
                        )}

                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setEditingUser(null)}>
                            Cancel
                          </Button>
                          <Button onClick={handleUpdateUser}>
                            Update User
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete User</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this user? This action cannot be undone.
                          All user data including profiles, roles, and subscriptions will be permanently removed.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDeleteUser(profile.user_id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete User
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredProfiles.length)} of {filteredProfiles.length} users
          </div>
          
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage > 1) setCurrentPage(currentPage - 1);
                  }}
                  className={currentPage <= 1 ? 'pointer-events-none opacity-50' : ''}
                />
              </PaginationItem>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    href="#"
                    isActive={page === currentPage}
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(page);
                    }}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                  }}
                  className={currentPage >= totalPages ? 'pointer-events-none opacity-50' : ''}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}